import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { isApiError } from '@/api/client';
import { ToastProvider } from '@/components/ui/toast';

import { ThemeProvider } from './theme';

/** 30 s stale time (doc 08); auth errors never retry. */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache(),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: (count, error) => {
          if (
            isApiError(error) &&
            (error.status === 401 ||
              error.status === 403 ||
              error.status === 404 ||
              error.status === 409)
          )
            return false;
          return count < 2;
        },
      },
      mutations: { retry: 0 },
    },
  });
}

export function AppProviders({
  children,
  client,
}: {
  children: ReactNode;
  client?: QueryClient | undefined;
}) {
  const [qc] = useState(() => client ?? makeQueryClient());
  return (
    <QueryClientProvider client={qc}>
      <ThemeProvider>
        <ToastProvider>{children}</ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
